<script setup>
import { ref, computed, watch } from 'vue'
import BcsModal from '../BcsModal.vue'
import BcsDropdownSelect from '../common/BcsDropdownSelect.vue'
import removeUserIcon from '../../../assets/images/remove-user.svg'

const props = defineProps({
  user: { type: Object, default: null },
  activeUsers: { type: Array, default: () => [] }
})

const emit = defineEmits(['confirm', 'cancel'])

const modalRef = ref(null)
const reassignOption = ref('unassigned')
const selectedOwner = ref('')
const isProcessing = ref(false)

const employerCount = computed(() => props.user?.employerCount || 0)

const firstName = computed(() => {
  if (!props.user?.fullname) return ''
  return props.user.fullname.split(' ')[0]
})

const availableOwners = computed(() => {
  if (!props.user) return []
  return props.activeUsers
    .filter(u => u.id !== props.user.id)
    .map(u => ({ value: u.email, label: `${u.fullname} (${u.email})` }))
})

const canSubmit = computed(() => {
  if (reassignOption.value === 'unassigned') return true
  return !!selectedOwner.value
})

watch(() => props.user, () => {
  reassignOption.value = 'unassigned'
  selectedOwner.value = ''
  isProcessing.value = false
})

function openModal() {
  reassignOption.value = 'unassigned'
  selectedOwner.value = ''
  isProcessing.value = false
  modalRef.value.openModal()
}

function closeModal() {
  modalRef.value.closeModal()
}

async function onConfirm() {
  if (!canSubmit.value || isProcessing.value) return
  isProcessing.value = true
  const reassignTo = reassignOption.value === 'reassign' ? selectedOwner.value : null
  emit('confirm', { user: props.user, reassignTo })
}

function onCancel() {
  closeModal()
  emit('cancel')
}

function setProcessing(val) {
  isProcessing.value = val
}

defineExpose({ openModal, closeModal, setProcessing })
</script>

<template>
  <BcsModal
    ref="modalRef"
    modalSize="small"
    modalType="confirmation"
    :hideCloseButton="false"
    :showFooter="false"
    :noDefaultStyles="true"
    @closeButtonClicked="onCancel"
  >
    <div class="text-start px-4 pb-2">
      <div class="text-center mb-2">
        <img :src="removeUserIcon" alt="Remove User" width="58" height="46" />
      </div>

      <p class="fw-bold header-24 text-center mb-1">Please confirm user removal</p>
      <p class="text-center text-muted text-14 mb-3">
        These employers will automatically be unassigned and you may reassign them in your Admin Center after user account removal.
      </p>
      <hr class="mb-4 mx-n4" />

      <div class="px-3">
        <p class="fw-bold mb-1 header-24" :class="{ 'text-center': employerCount === 0 }">{{ user?.fullname }} will be removed.</p>
        <p v-if="employerCount > 0" class="text-danger fw-bold text-16 my-3">
          This user currently has {{ employerCount }} assigned employer{{ employerCount !== 1 ? 's' : '' }}.
        </p>
        <p v-else class="text-muted text-16 my-3 text-center">
          This user has no assigned employers.
        </p>

        <div v-if="employerCount > 0">
          <p class="fw-bold text-14 mb-2">Reassign {{ firstName }}'s Employers</p>

          <div class="form-check mb-1.5">
            <input
              class="form-check-input"
              type="radio"
              id="optionUnassigned"
              value="unassigned"
              v-model="reassignOption"
            />
            <label class="form-check-label fw-semibold text-16" for="optionUnassigned">
              Unassigned
            </label>
            <p class="text-muted text-14 mb-0">
              The employers assigned to this user will be moved to 'Unassigned'.
            </p>
          </div>

          <div class="form-check mb-2">
            <input
              class="form-check-input"
              type="radio"
              id="optionReassign"
              value="reassign"
              v-model="reassignOption"
            />
            <label class="form-check-label fw-semibold text-16" for="optionReassign">
              Choose New Account Owner
            </label>
            <p class="text-muted text-14 mb-0">
              The employers assigned to this user will be assigned to a new account owner of your choice.
            </p>
          </div>

          <div class="mb-4 px-4">
            <label class="form-label fw-bold text-14">New Account Owner</label>
            <BcsDropdownSelect
              :modelValue="selectedOwner"
              @update:modelValue="selectedOwner = $event"
              :options="availableOwners"
              placeholder="Select an Account Owner"
              :disabled="reassignOption !== 'reassign'"
            />
          </div>
        </div>

      </div>
      <hr class="mb-4 mx-n4" />

      <div class="d-flex justify-content-center gap-3 mt-3 mb-2">
        <button
          type="button"
          class="btn btn-outline-primary px-5"
          :disabled="isProcessing"
          @click="onCancel"
        >
          Nevermind
        </button>
        <button
          type="button"
          class="btn btn-danger px-5"
          :disabled="!canSubmit || isProcessing"
          @click="onConfirm"
        >
          Remove User
          <span v-if="isProcessing" class="spinner-border spinner-border-sm ms-2" role="status"></span>
        </button>
      </div>
    </div>
  </BcsModal>
</template>
